import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import { MdAdd, MdLock } from 'react-icons/md';
import Checkbox from '@/components/Checkbox';
import useProvidedJobPostLists from '../api/useProvidedJobPostLists';
import useBulkAddToLists from '../api/useBulkAddToLists';
import { showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import CreateListDialog from './CreateListDialog';
import {
  selectionCount,
  isAllMode,
  type Selection,
} from '@/features/agent/provided_job_posts/hooks/useSelection';
import type { SearchQuery } from '@/features/agent/provided_job_posts/hooks/useProvidedJobPosts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selection: Selection;
  searchQuery: SearchQuery;
  onCompleted?: () => void;
}

export default function AddToListDialog({
  open,
  onOpenChange,
  selection,
  searchQuery,
  onCompleted,
}: Props) {
  const { data: lists, isLoading } = useProvidedJobPostLists();
  const { mutateAsync: bulkAdd, isPending } = useBulkAddToLists();
  const [selectedListIds, setSelectedListIds] = useState<Set<number>>(new Set());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const count = selectionCount(selection);

  const handleToggle = useCallback((listId: number) => {
    setSelectedListIds((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) {
        next.delete(listId);
      } else {
        next.add(listId);
      }
      return next;
    });
  }, []);

  const handleSubmit = async () => {
    if (selectedListIds.size === 0) return;
    // 1,000件超の場合は確認ダイアログを表示
    if (count > 1000) {
      if (!window.confirm(`${count}件をリストに追加します。よろしいですか？`)) return;
    }
    try {
      if (isAllMode(selection)) {
        // 全件選択モード: フィルタ条件 + 除外IDで送信
        await bulkAdd({
          listIds: Array.from(selectedListIds),
          searchQuery,
          excludedProvidedJobPostIds: Array.from(selection.excludedIds),
        });
      } else {
        // 個別選択モード: 選択IDを直接送信
        await bulkAdd({
          listIds: Array.from(selectedListIds),
          providedJobPostIds: Array.from(selection.ids),
        });
      }
      setSelectedListIds(new Set());
      onOpenChange(false);
      showSuccessToast(`${count}件をリストに追加しました`);
      onCompleted?.();
    } catch {
      alert('リストへの追加に失敗しました');
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedListIds(new Set());
    }
    onOpenChange(nextOpen);
  };

  // manual モード時のみ CreateListDialog に渡す用の配列
  const manualJobPostIds = selection.mode === 'manual' ? Array.from(selection.ids) : [];

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="w-[640px] rounded-sm">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>リストに追加</DialogTitle>
              <Button
                color="secondary"
                size="md"
                onClick={() => setIsCreateDialogOpen(true)}
                disabled={isAllMode(selection)}
                title={
                  isAllMode(selection)
                    ? '全選択モードでは新しいリストへの追加はできません'
                    : undefined
                }
              >
                <MdAdd className="size-icon-xs" />
                新しいリストを作成
              </Button>
            </div>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto px-lg pt-2 pb-lg">
            <p className="py-4 text-sm">
              <span className="font-bold">{count}件</span>
              をリストに追加します。追加するリストを選択してください。
            </p>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loading size="small" />
              </div>
            ) : !lists || lists.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">
                選択できるリストがありません
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {lists.map((list) => (
                  <div
                    key={list.id}
                    role="button"
                    tabIndex={0}
                    className="flex cursor-pointer items-center justify-between rounded border border-solid border-border p-4 font-normal hover:bg-gray-50"
                    onClick={() => handleToggle(list.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle(list.id);
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="flex"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        <Checkbox
                          checked={selectedListIds.has(list.id)}
                          onChange={() => handleToggle(list.id)}
                        />
                      </span>
                      <span className="text-base font-bold">{list.name}</span>
                      {!list.is_public && <MdLock className="size-4" title="非公開" />}
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex w-[94px] items-center gap-2 text-sm">
                        <span>求人数</span>
                        <span className="font-bold">{list.provided_job_posts_count}</span>
                      </div>
                      <div className="flex w-[140px] items-center gap-2 text-sm">
                        <span>作成者</span>
                        <span className="rounded-full border border-solid border-border px-2.5 py-0.5 text-[11px]">
                          {list.created_by_name}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <div className="flex w-full justify-end gap-2">
              <Button color="secondary" size="md" onClick={() => handleOpenChange(false)}>
                キャンセル
              </Button>
              <Button
                color="primary"
                size="md"
                onClick={handleSubmit}
                disabled={selectedListIds.size === 0 || isPending}
              >
                {isPending ? '追加中...' : '追加して完了'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateListDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        providedJobPostIds={manualJobPostIds}
        onCreated={() => {
          setIsCreateDialogOpen(false);
          setSelectedListIds(new Set());
          onOpenChange(false);
          onCompleted?.();
        }}
      />
    </>
  );
}
